import { BOOKING_API_URL, supabase } from '../lib/supabase';
import type {
  CheckinError,
  CheckinPrepareResponse,
  CheckinResponse,
} from '../types';

export interface CheckinResult<T = CheckinResponse> {
  ok: boolean;
  data: T | null;
  error: CheckinError | null;
  status: number;
}

async function postCheckin<T>(
  body: Record<string, string | null>,
): Promise<CheckinResult<T>> {
  const { data: sessionData } = await supabase.auth.getSession();
  const jwt = sessionData.session?.access_token;
  if (!jwt) {
    return {
      ok: false,
      data: null,
      error: { error: 'unauthenticated', message: 'No active session' },
      status: 401,
    };
  }

  let res: Response;
  try {
    res = await fetch(`${BOOKING_API_URL}/booking-checkin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    return {
      ok: false,
      data: null,
      error: {
        error: 'network_error',
        message: e instanceof Error ? e.message : String(e),
      },
      status: 0,
    };
  }

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    // Empty / non-JSON response. Fall through with null.
  }

  if (!res.ok) {
    return {
      ok: false,
      data: null,
      error: (json as CheckinError) ?? {
        error: 'unknown_error',
        message: `HTTP ${res.status}`,
      },
      status: res.status,
    };
  }

  return {
    ok: true,
    data: json as T,
    error: null,
    status: res.status,
  };
}

// playLanguage: lowercase 2-letter code confirmed at the desk. Omitted =
// departures.play_language stays as it is (locale default applies).
export function checkinBooking(qrToken: string, playLanguage?: string): Promise<CheckinResult> {
  const body: Record<string, string> = { qr_token: qrToken, mode: 'checkin' };
  if (playLanguage) body.play_language = playLanguage;
  return postCheckin<CheckinResponse>(body);
}

export function checkinBookingById(bookingId: string, playLanguage?: string): Promise<CheckinResult> {
  const body: Record<string, string> = { booking_id: bookingId, mode: 'checkin' };
  if (playLanguage) body.play_language = playLanguage;
  return postCheckin<CheckinResponse>(body);
}

// Read-only lookup for CheckinFlowModal — fetches booking metadata + the
// list of eligible earlier slots so the operator can bump an early arrival
// before the booking transitions to 'arrived'.
export function prepareCheckin(args: {
  qr_token?: string;
  booking_id?: string;
}): Promise<CheckinResult<CheckinPrepareResponse>> {
  const body: Record<string, string> = { mode: 'prepare' };
  if (args.qr_token)   body.qr_token   = args.qr_token;
  if (args.booking_id) body.booking_id = args.booking_id;
  return postCheckin<CheckinPrepareResponse>(body);
}
